import { useEffect, useState, useMemo, useCallback, useRef } from "react"
import { useAuth } from "@/context/auth/auth-context"
import { useCurrency } from "@/context/currency/currency-context"
import hive from "@/lib/api/hive"
import { type Portfolio, type CalendarEvent, type EarningsEvent } from "@/types/portfolio"
import { CalendarGrid } from "@/components/custom/calendar/calendar-grid"
import { Card, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { ChevronLeft, ChevronRight, CalendarDays, Loader2, Download } from "lucide-react"
import { exportCalendarEventsAsCSV } from "@/lib/csv-export"
import { cn } from "@/lib/utils"
import { ICSSubscription } from "@/components/custom/calendar/ics-subscription"
import { Analytics } from "@/lib/analytics"

const MONTH_NAMES = [
	"January",
	"February",
	"March",
	"April",
	"May",
	"June",
	"July",
	"August",
	"September",
	"October",
	"November",
	"December",
]

export interface CalendarDisplayEvent extends CalendarEvent {
	date_type: "payment" | "ex-date" | "earnings"
	event_type: "dividend" | "earnings"
	// Earnings-specific fields (only set when event_type === "earnings")
	eps_estimate?: number | null
	eps_actual?: number | null
	revenue_estimate?: number | null
	revenue_actual?: number | null
	is_upcoming?: boolean
	company_name?: string
	earnings_time?: "amc" | "bmo" | "unknown"
}

function groupEventsByDate(
	events: CalendarEvent[],
	earningsEvents: EarningsEvent[],
): Map<string, CalendarDisplayEvent[]> {
	const map = new Map<string, CalendarDisplayEvent[]>()

	const addEvent = (dateStr: string, displayEvent: CalendarDisplayEvent) => {
		if (!dateStr) return
		// Slice to YYYY-MM-DD rather than parsing through Date, which would apply
		// the local timezone offset and shift midnight-UTC dates by ±1 day.
		const key = dateStr.slice(0, 10)
		const existing = map.get(key)
		if (existing) {
			existing.push(displayEvent)
		} else {
			map.set(key, [displayEvent])
		}
	}

	for (const event of events) {
		addEvent(event.payment_date, { ...event, date_type: "payment", event_type: "dividend" })
		if (event.ex_date && event.ex_date !== "0001-01-01T00:00:00Z") {
			addEvent(event.ex_date, { ...event, date_type: "ex-date", event_type: "dividend" })
		}
	}

	const seenEarnings = new Set<string>()
	for (const earning of earningsEvents) {
		const key = `${earning.ticker}:${earning.date.substring(0, 10)}`
		if (seenEarnings.has(key)) continue
		seenEarnings.add(key)
		// Create a CalendarDisplayEvent from earnings data using a synthetic CalendarEvent shape
		const displayEvent: CalendarDisplayEvent = {
			ticker: earning.ticker,
			exchange: earning.exchange,
			amount: 0,
			payment_date: earning.date,
			ex_date: "",
			declaration_date: "",
			is_actual: !earning.is_upcoming,
			status: "declared",
			portfolio_id: "",
			portfolio_name: "",
			date_type: "earnings",
			event_type: "earnings",
			eps_estimate: earning.eps_estimate,
			eps_actual: earning.eps_actual,
			revenue_estimate: earning.revenue_estimate,
			revenue_actual: earning.revenue_actual,
			is_upcoming: earning.is_upcoming,
			company_name: earning.company_name,
			earnings_time: earning.time,
		}
		addEvent(earning.date, displayEvent)
	}

	return map
}

export default function CalendarPage() {
	const { user, token, isAuthenticated } = useAuth()
	const { format, convert } = useCurrency()

	const [portfolios, setPortfolios] = useState<Portfolio[]>([])
	const [allEvents, setAllEvents] = useState<CalendarEvent[]>([])
	const [earningsEvents, setEarningsEvents] = useState<EarningsEvent[]>([])
	const [isLoading, setIsLoading] = useState(true)
	const [isNavigating, setIsNavigating] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [activeFilters, setActiveFilters] = useState<Set<string>>(
		new Set(["paid", "declared", "predicted", "earnings"]),
	)

	const now = new Date()
	const [viewYear, setViewYear] = useState(now.getFullYear())
	const [viewMonth, setViewMonth] = useState(now.getMonth())
	const [selectedPortfolioId, setSelectedPortfolioId] = useState<string>("all")

	const [touchStart, setTouchStart] = useState<number | null>(null)
	const [touchEnd, setTouchEnd] = useState<number | null>(null)
	const [swipeDirection, setSwipeDirection] = useState<"left" | "right" | null>(null)
	const minSwipeDistance = 50

	const isFirstFetch = useRef(true)

	const fetchData = useCallback(async () => {
		if (!isAuthenticated || !token || !user) return

		const isFirst = isFirstFetch.current
		isFirstFetch.current = false

		try {
			if (isFirst) {
				setIsLoading(true)
			} else {
				setIsNavigating(true)
			}
			setError(null)

			// Pad date range by 7 days on each side for leading/trailing calendar days
			const from = new Date(viewYear, viewMonth, -6)
			const to = new Date(viewYear, viewMonth + 1, 7)
			const fromStr = from.toISOString().split("T")[0]
			const toStr = to.toISOString().split("T")[0]

			const portfolioFilter = selectedPortfolioId !== "all" ? selectedPortfolioId : undefined

			const [portfolioData, events, earnings] = await Promise.all([
				hive.getPortfolios(token, user.id),
				hive.fetchCalendarEvents(fromStr, toStr, token, portfolioFilter),
				hive.fetchEarningsCalendar(fromStr, toStr, token, portfolioFilter),
			])

			setPortfolios(portfolioData)
			setAllEvents(events)
			setEarningsEvents(earnings)
		} catch (err) {
			setError(err instanceof Error ? err.message : "Failed to load calendar data")
		} finally {
			if (isFirst) {
				setIsLoading(false)
			} else {
				setIsNavigating(false)
			}
		}
	}, [isAuthenticated, token, user, viewYear, viewMonth, selectedPortfolioId])

	useEffect(() => {
		fetchData()
	}, [fetchData])

	const allEventsByDate = useMemo(() => groupEventsByDate(allEvents, earningsEvents), [allEvents, earningsEvents])

	// Apply filters
	const eventsByDate = useMemo(() => {
		const filtered = new Map<string, CalendarDisplayEvent[]>()
		allEventsByDate.forEach((events, key) => {
			const filteredEvents = events.filter((e) => {
				if (e.event_type === "earnings") return activeFilters.has("earnings")
				if (e.date_type === "ex-date") {
					// Ex-dates follow their parent dividend's status filter
					return activeFilters.has(e.status)
				}
				return activeFilters.has(e.status)
			})
			if (filteredEvents.length > 0) {
				filtered.set(key, filteredEvents)
			}
		})
		return filtered
	}, [allEventsByDate, activeFilters])

	// Stats for current month
	const monthStats = useMemo(() => {
		let paid = 0
		let declared = 0
		let predicted = 0
		let paymentDates = 0
		let exDates = 0
		let earningsCount = 0

		allEventsByDate.forEach((events, key) => {
			const [y, m] = key.split("-").map(Number)
			if (y !== viewYear || m !== viewMonth + 1) return

			const paymentEvents = events.filter((e) => e.date_type === "payment")
			const exDateEvents = events.filter((e) => e.date_type === "ex-date")
			const earningEvents = events.filter((e) => e.event_type === "earnings")

			if (paymentEvents.length > 0) paymentDates++
			if (exDateEvents.length > 0) exDates++
			earningsCount += earningEvents.length

			for (const e of paymentEvents) {
				const amount = convert(e.amount, e.currency)
				switch (e.status) {
					case "paid":
						paid += amount
						break
					case "declared":
						declared += amount
						break
					case "predicted":
						predicted += amount
						break
				}
			}
		})

		return { paid, declared, predicted, total: paid + declared + predicted, paymentDates, exDates, earningsCount }
	}, [allEventsByDate, viewYear, viewMonth, convert])

	const toggleFilter = (filter: string) => {
		const enabled = !activeFilters.has(filter)
		setActiveFilters((prev) => {
			const next = new Set(prev)
			if (next.has(filter)) {
				next.delete(filter)
			} else {
				next.add(filter)
			}
			return next
		})
		Analytics.capture("Calendar Filter Toggled", { filter, enabled })
	}

	const handlePrevMonth = () => {
		if (viewMonth === 0) {
			setViewMonth(11)
			setViewYear((y) => y - 1)
		} else {
			setViewMonth((m) => m - 1)
		}
		Analytics.capture("Calendar Navigated", { direction: "prev" })
	}

	const handleNextMonth = () => {
		if (viewMonth === 11) {
			setViewMonth(0)
			setViewYear((y) => y + 1)
		} else {
			setViewMonth((m) => m + 1)
		}
		Analytics.capture("Calendar Navigated", { direction: "next" })
	}

	const handleToday = () => {
		const now = new Date()
		setViewYear(now.getFullYear())
		setViewMonth(now.getMonth())
	}

	const handleExportCSV = () => {
		const filename = `beediv-calendar-${viewYear}-${String(viewMonth + 1).padStart(2, "0")}.csv`
		const monthEvents = allEvents.filter((event) => {
			const d = new Date(event.payment_date)
			return d.getFullYear() === viewYear && d.getMonth() === viewMonth
		})
		exportCalendarEventsAsCSV(monthEvents, filename)
		Analytics.capture("Calendar CSV Exported")
	}

	const onTouchStart = (e: React.TouchEvent) => {
		setTouchEnd(null)
		setTouchStart(e.targetTouches[0].clientX)
	}
	const onTouchMove = (e: React.TouchEvent) => {
		setTouchEnd(e.targetTouches[0].clientX)
	}
	const onTouchEnd = () => {
		if (!touchStart || !touchEnd) return
		const distance = touchStart - touchEnd
		if (distance > minSwipeDistance) {
			setSwipeDirection("left")
			setTimeout(() => {
				handleNextMonth()
				setSwipeDirection(null)
			}, 300)
		}
		if (distance < -minSwipeDistance) {
			setSwipeDirection("right")
			setTimeout(() => {
				handlePrevMonth()
				setSwipeDirection(null)
			}, 300)
		}
	}

	if (isLoading) {
		return (
			<div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
				<Loader2 className="h-8 w-8 animate-spin text-sky-600" />
				<p className="text-muted-foreground animate-pulse">Loading your dividend calendar...</p>
			</div>
		)
	}

	if (error) {
		return (
			<div className="container mx-auto px-4 py-12 text-center">
				<h2 className="text-2xl font-bold text-red-600 mb-2">Oops! Something went wrong</h2>
				<p className="text-muted-foreground mb-6">{error}</p>
				<Button onClick={() => window.location.reload()} variant="outline">
					Try Again
				</Button>
			</div>
		)
	}

	return (
		<div className="container mx-auto px-4 py-8 space-y-6">
			{/* Header */}
			<div className="flex flex-col md:flex-row justify-between items-start md:items-end border-b border-sky-900/10 dark:border-sky-100/10 pb-4 gap-4">
				<div className="flex items-center gap-3">
					<div className="bg-sky-100 dark:bg-sky-900/50 p-2 rounded-lg">
						<CalendarDays className="h-6 w-6 text-sky-600" />
					</div>
					<div>
						<h1 className="text-3xl font-bold text-sky-950 dark:text-sky-50">Dividend Calendar</h1>
						<p className="text-muted-foreground">Track upcoming nectar from your hives</p>
					</div>
				</div>

				<div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
					{portfolios.length > 1 && (
						<Select
							value={selectedPortfolioId}
							onValueChange={(v) => {
								setSelectedPortfolioId(v)
								Analytics.capture("Calendar Portfolio Changed")
							}}
						>
							<SelectTrigger className="w-full sm:w-[200px]">
								<SelectValue placeholder="All Portfolios" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">All Portfolios</SelectItem>
								{portfolios.map((p) => (
									<SelectItem key={p.id} value={p.id}>
										{p.name}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					)}
					<ICSSubscription />
					{allEvents.length > 0 && (
						<Button variant="outline" size="sm" className="gap-2" onClick={handleExportCSV}>
							<Download className="h-4 w-4" />
							Export CSV
						</Button>
					)}
				</div>
			</div>

			{/* Month Stats */}
			<div className="grid grid-cols-2 md:grid-cols-6 gap-3">
				<Card className="bg-white/60 dark:bg-black/20 backdrop-blur-md border-sky-900/10 dark:border-sky-100/10">
					<CardHeader className="pb-2 pt-4 px-4">
						<CardDescription className="text-xs">Total This Month</CardDescription>
						<CardTitle className="text-xl text-sky-600 dark:text-sky-400">
							{format(monthStats.total, { skipConversion: true })}
						</CardTitle>
					</CardHeader>
				</Card>
				<Card className="bg-white/60 dark:bg-black/20 backdrop-blur-md border-sky-900/10 dark:border-sky-100/10">
					<CardHeader className="pb-2 pt-4 px-4">
						<CardDescription className="text-xs">Payment Dates</CardDescription>
						<CardTitle className="text-xl text-sky-600 dark:text-sky-400">
							{monthStats.paymentDates}
						</CardTitle>
					</CardHeader>
				</Card>
				<Card className="bg-white/60 dark:bg-black/20 backdrop-blur-md border-sky-900/10 dark:border-sky-100/10">
					<CardHeader className="pb-2 pt-4 px-4">
						<CardDescription className="text-xs flex items-center gap-1.5">
							<span className="w-2 h-2 rounded-full bg-green-500 inline-block" />
							Paid
						</CardDescription>
						<CardTitle className="text-xl text-green-600 dark:text-green-400">
							{format(monthStats.paid, { skipConversion: true })}
						</CardTitle>
					</CardHeader>
				</Card>
				<Card className="bg-white/60 dark:bg-black/20 backdrop-blur-md border-sky-900/10 dark:border-sky-100/10">
					<CardHeader className="pb-2 pt-4 px-4">
						<CardDescription className="text-xs flex items-center gap-1.5">
							<span className="w-2 h-2 rounded-full bg-[#bae6fd] inline-block" />
							Upcoming
						</CardDescription>
						<CardTitle className="text-xl text-sky-600 dark:text-sky-400">
							{format(monthStats.declared + monthStats.predicted, { skipConversion: true })}
						</CardTitle>
					</CardHeader>
				</Card>
				<Card className="bg-white/60 dark:bg-black/20 backdrop-blur-md border-sky-900/10 dark:border-sky-100/10">
					<CardHeader className="pb-2 pt-4 px-4">
						<CardDescription className="text-xs flex items-center gap-1.5">
							<span className="w-2 h-2 rounded-sm border border-dashed border-sky-600 inline-block" />
							Ex-Dates
						</CardDescription>
						<CardTitle className="text-xl text-sky-600 dark:text-sky-400">{monthStats.exDates}</CardTitle>
					</CardHeader>
				</Card>
				<Card className="bg-white/60 dark:bg-black/20 backdrop-blur-md border-sky-900/10 dark:border-sky-100/10">
					<CardHeader className="pb-2 pt-4 px-4">
						<CardDescription className="text-xs flex items-center gap-1.5">
							<span className="w-2 h-2 rounded-full bg-indigo-500 inline-block" />
							Earnings
						</CardDescription>
						<CardTitle className="text-xl text-indigo-600 dark:text-indigo-400">
							{monthStats.earningsCount}
						</CardTitle>
					</CardHeader>
				</Card>
			</div>

			{/* Filter Chips */}
			<div className="flex items-center gap-1.5 md:gap-2">
				<span className="hidden md:inline text-xs text-muted-foreground shrink-0 mr-1">Show:</span>
				<button
					onClick={() => toggleFilter("paid")}
					className={cn(
						"flex-1 md:flex-none inline-flex items-center justify-center md:justify-start gap-1.5 px-2 md:px-3 py-1 rounded-full text-xs font-medium transition-colors border",
						activeFilters.has("paid")
							? "bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400 border-green-500/30"
							: "bg-muted/50 text-muted-foreground border-transparent",
					)}
				>
					<span className="w-2 h-2 rounded-full bg-green-500" />
					Paid
				</button>
				<button
					onClick={() => toggleFilter("declared")}
					className={cn(
						"flex-1 md:flex-none inline-flex items-center justify-center md:justify-start gap-1.5 px-2 md:px-3 py-1 rounded-full text-xs font-medium transition-colors border",
						activeFilters.has("declared")
							? "bg-[#0ea5e9]/10 text-[#0ea5e9] dark:text-sky-400 border-[#0ea5e9]/30"
							: "bg-muted/50 text-muted-foreground border-transparent",
					)}
				>
					<span className="w-2 h-2 rounded-full bg-[#0ea5e9]" />
					Declared
				</button>
				<button
					onClick={() => toggleFilter("predicted")}
					className={cn(
						"flex-1 md:flex-none inline-flex items-center justify-center md:justify-start gap-1.5 px-2 md:px-3 py-1 rounded-full text-xs font-medium transition-colors border",
						activeFilters.has("predicted")
							? "bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-200 border-sky-300/60 dark:border-sky-700/50"
							: "bg-muted/50 text-muted-foreground border-transparent",
					)}
				>
					<span className="w-2 h-2 rounded-full bg-[#bae6fd]" />
					Predicted
				</button>
				<button
					onClick={() => toggleFilter("earnings")}
					className={cn(
						"flex-1 md:flex-none inline-flex items-center justify-center md:justify-start gap-1.5 px-2 md:px-3 py-1 rounded-full text-xs font-medium transition-colors border",
						activeFilters.has("earnings")
							? "bg-indigo-500/10 text-indigo-600 dark:text-indigo-300 border-indigo-500/30"
							: "bg-muted/50 text-muted-foreground border-transparent",
					)}
				>
					<span className="w-2 h-2 rounded-full bg-indigo-500" />
					Earnings
				</button>
			</div>

			{/* Month Navigation */}
			<div className="flex items-center justify-between">
				<div className="flex items-center gap-2">
					<Button variant="outline" size="icon" onClick={handlePrevMonth} className="h-8 w-8">
						<ChevronLeft className="h-4 w-4" />
					</Button>
					<span className="text-lg font-semibold text-sky-950 dark:text-sky-50 min-w-[180px] text-center">
						{MONTH_NAMES[viewMonth]} {viewYear}
					</span>
					<Button variant="outline" size="icon" onClick={handleNextMonth} className="h-8 w-8">
						<ChevronRight className="h-4 w-4" />
					</Button>
				</div>
				<Button variant="outline" size="sm" onClick={handleToday}>
					Today
				</Button>
			</div>

			{/* Calendar Grid */}
			<div className="relative">
				{isNavigating && (
					<div className="absolute inset-0 z-10 flex items-center justify-center bg-background/40 backdrop-blur-[1px] rounded-lg">
						<Loader2 className="h-6 w-6 animate-spin text-sky-500" />
					</div>
				)}
				<div
					onTouchStart={onTouchStart}
					onTouchMove={onTouchMove}
					onTouchEnd={onTouchEnd}
					className={`transition-transform duration-300 ${
						swipeDirection === "left"
							? "-translate-x-4 opacity-70"
							: swipeDirection === "right"
								? "translate-x-4 opacity-70"
								: ""
					}`}
				>
					<CalendarGrid year={viewYear} month={viewMonth} eventsByDate={eventsByDate} />
				</div>
			</div>
		</div>
	)
}
